/**
 * Chirp theme based on WarrChirp/WarrQueue.
 * Light mode uses dark blue (#182955) for buttons/icons.
 * Dark mode matches WarrQueue identically.
 */
export const ChirpTheme = {
  palette: {
    mode: 'light' as const,
    primary: {
      main: '#182955',
      light: '#2D4A8F',
      dark: '#0F1A35',
      contrast: '#FFFFFF',
      contrastText: '#FFFFFF',
      chipBg: '#18295514',
      tint08: '#18295514',
      highlight: '#18295580',
      selected: '#264490',
      hover: '#1E327014',
      hoverBorder: '#18295580',
      hoverContainedBg: '#142347',
      hoverContainedBorder: '#142347',
      focus: '#1829551F',
      focusVisible: '#1829554D',
      outlinedBorder: '#18295580',
    },
    background: {
      default: '#F7F7F7',
      paper: '#FFFFFF',
      paperElevation0: '#FFFFFF',
      paperElevation1: '#FAFAFA',
      paperElevation2: '#F5F5F5',
      paperElevation16: '#EEEEEE',
      paperElevation24: '#EDEDED',
      backdrop: '#F5F5F5',
    },
    border: {
      outlined: '#BDBDBD',
    },
    secondary: {
      main: '#B54FBA',
      dark: '#7B1FA2',
      light: '#BA68C8',
      contrast: '#FFFFFF',
      grass: '#72CF76',
      highlight: '#ECF1FC',
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#C62828',
      contrast: '#FFFFFF',
    },
    warning: {
      main: '#EF6C00',
      light: '#FF9800',
      dark: '#E65100',
      contrast: '#FFFFFF',
    },
    info: {
      main: '#182955',
      light: '#03A9F4',
      dark: '#0F1A35',
      contrast: '#000000DE',
    },
    success: {
      main: '#43A047',
      light: '#4CAF50',
      dark: '#1B5E20',
      contrast: '#FFFFFF',
    },
    action: {
      active: '#0000008F',
      hover: '#00000014',
      selected: '#00000029',
      disabled: '#00000061',
      disabledBackground: '#0000001F',
      focus: '#0000001F',
    },
    text: {
      primary: '#000000DE',
      secondary: '#0000008F',
      disabled: '#00000061',
    },
    common: {
      white: '#FFFFFF',
      black: '#000000',
    },
    divider: '#0000001F',
    header: { blue: '#182955' },
    workItemRow: {
      assigned: '#B54FBA20',
      assignedHover: '#B54FBA30',
      hoverNeutral: '#00000026',
    },
    workItemType: {
      error: '#FDEDED',
      info: '#E5F6FD',
      default: '#FFFFFF',
    },
    carrot: {
      main: '#FB9702',
    },
  },
  typography: {
    h5: {
      fontSize: '.88rem',
    },
  },
};

export const ChirpDarkTheme = {
  palette: {
    mode: 'dark' as const,
    primary: {
      main: '#85A7FF',
      light: '#D6E2FF',
      dark: '#4D7FFF',
      contrast: '#000000DE',
      contrastText: '#000000DE',
      chipBg: '#85A7FF14',
      tint08: '#85A7FF14',
      selected: '#84A6FF29',
      highlight: '#4271EB80',
      hover: '#84A6FF14',
      hoverBorder: '#84A6FF80',
      hoverContainedBg: '#4D7FFF',
      hoverContainedBorder: '#4D7FFF',
      focus: '#84A6FF1F',
      focusVisible: '#84A6FF4D',
      outlinedBorder: '#84A6FF80',
    },
    secondary: {
      main: '#CE93D8',
      dark: '#AB47BC',
      light: '#BA68C8',
      contrast: '#000000DE',
      grass: '#72CF76',
      highlight: '#1F2230',
    },
    error: {
      main: '#F44336',
      light: '#E57373',
      dark: '#D32F2F',
    },
    warning: {
      main: '#FFA726',
      light: '#FFB74D',
      dark: '#F57C00',
      contrast: '#000000DE',
    },
    info: {
      main: '#85A7FF',
      light: '#B2C8FF',
      dark: '#4D7FFF',
      contrast: '#000000DE',
    },
    success: {
      main: '#66BB6A',
      light: '#81C784',
      dark: '#388E3C',
      contrast: '#000000DE',
    },
    common: {
      white: '#FFFFFF',
      black: '#000000',
    },
    header: { blue: '#182955' },
    background: {
      default: '#1E1E1E',
      paper: '#121212',
      paperElevation0: '#121212',
      paperElevation1: '#1E1E1E',
      paperElevation2: '#232323',
      paperElevation16: '#363636',
      paperElevation24: '#383838',
      backdrop: '#181818',
    },
    action: {
      active: '#FFFFFF8F',
      hover: '#FFFFFF14',
      selected: '#FFFFFF29',
      disabled: '#FFFFFF54',
      disabledBackground: '#FFFFFF1F',
      focus: '#FFFFFF1F',
      divider: '#FFFFFF1F',
    },
    text: {
      primary: '#FFFFFFDE',
      secondary: '#FFFFFF8F',
      disabled: '#FFFFFF61',
    },
    divider: '#FFFFFF1F',
    border: {
      outlined: '#757575',
    },
    workItemRow: {
      assigned: '#CE93D80F',
      assignedHover: '#CE93D814',
      hoverNeutral: '#FFFFFF14',
    },
    workItemType: {
      error: '#3D1F1F',
      info: '#1F2A33',
      default: '#121212',
    },
    carrot: {
      main: '#FFB74D',
    },
  },
  typography: {
    h5: {
      fontSize: '.88rem',
    },
  },
};
